import React, { useState } from 'react';
import {
    Button,
    IconButton,
    Tooltip,
    Menu,
    MenuItem,
    useMediaQuery,
    useTheme,
    Box,
    alpha,
    Skeleton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import { usePermissions } from '../../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../../utils/permissions';

const NewProjectButton = styled(Button)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    borderRadius: '8px',
    padding: '10px 16px',
    fontWeight: 600,
    fontSize: '0.875rem',
    textTransform: 'none',
    boxShadow: 'none',
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: theme.palette.primary.dark,
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    },
}));

const FilterButton = styled(IconButton)(({ theme }) => ({
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: '8px',
    padding: '8px',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.grey[600],
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
        color: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
    },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
    fontSize: '0.875rem',
    padding: '10px 16px',
    borderRadius: '4px',
    margin: '2px 4px',
    '&.Mui-selected': {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
        color: theme.palette.primary.main,
        fontWeight: 500,
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
        },
    },
    '&:hover': {
        backgroundColor: theme.palette.grey[50],
    },
}));

const getFilterButtonStyles = (theme, isFiltered) => ({
    backgroundColor: isFiltered
        ? alpha(theme.palette.primary.main, 0.04)
        : theme.palette.common.white,
    color: isFiltered ? theme.palette.primary.main : theme.palette.grey[600],
    borderColor: isFiltered ? theme.palette.primary.main : theme.palette.grey[200],
});

const ActionButtons = ({ onCreateProject, onFilterOption, currentFilter = 'All' }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const isFiltered = currentFilter !== 'All';
    const { canBusiness, isLoading } = usePermissions();

    // Check if user can create new projects (requires ADMIN permission)
    const canCreateProject = canBusiness(PERMISSIONS.ADMIN);

    const handleFilterClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleFilterOption = (option) => {
        onFilterOption(option);
        handleClose();
    };

    // Calculate filter label based on current filter
    const getFilterLabel = () => {
        switch (currentFilter) {
            case 'Active':
                return 'Active Only';
            case 'DateAsc':
                return 'Date (Asc)';
            case 'DateDesc':
                return 'Date (Desc)';
            case 'Notifications':
                return 'By Notifications';
            default:
                return 'Filter';
        }
    };

    return (
        <Box width="100%" display="flex" justifyContent={isMobile ? 'space-between' : 'flex-end'}>
            {/* New Project button - only visible to users with ADMIN permission */}
            {isLoading ? (
                <Skeleton
                    variant="rectangular"
                    width={isMobile ? '100%' : 150}
                    height={40}
                    sx={{ borderRadius: '8px' }}
                />
            ) : (
                canCreateProject && (
                    <NewProjectButton
                        startIcon={<AddIcon />}
                        onClick={onCreateProject}
                        fullWidth={isMobile}
                        disableElevation
                    >
                        New Project
                    </NewProjectButton>
                )
            )}

            <Box ml={isMobile ? 1 : 2}>
                <Tooltip title={`Current filter: ${getFilterLabel()}`} arrow>
                    <FilterButton
                        aria-label="filter projects"
                        onClick={handleFilterClick}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                        sx={getFilterButtonStyles(theme, isFiltered)}
                    >
                        <FilterListIcon fontSize="small" />
                    </FilterButton>
                </Tooltip>
            </Box>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    elevation: 2,
                    sx: {
                        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.06)',
                        borderRadius: '12px',
                        minWidth: 200,
                        padding: '4px',
                        mt: 1,
                        border: `1px solid ${theme.palette.grey[100]}`,
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <StyledMenuItem
                    onClick={() => handleFilterOption('All')}
                    selected={currentFilter === 'All'}
                >
                    All Projects
                </StyledMenuItem>
                <StyledMenuItem
                    onClick={() => handleFilterOption('Active')}
                    selected={currentFilter === 'Active'}
                >
                    Active Only
                </StyledMenuItem>
                <StyledMenuItem
                    onClick={() => handleFilterOption('DateAsc')}
                    selected={currentFilter === 'DateAsc'}
                >
                    Date (Ascending)
                </StyledMenuItem>
                <StyledMenuItem
                    onClick={() => handleFilterOption('DateDesc')}
                    selected={currentFilter === 'DateDesc'}
                >
                    Date (Descending)
                </StyledMenuItem>
                <StyledMenuItem
                    onClick={() => handleFilterOption('Notifications')}
                    selected={currentFilter === 'Notifications'}
                >
                    Most Notifications
                </StyledMenuItem>
            </Menu>
        </Box>
    );
};

export default ActionButtons;
